/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { AdcModel, ShuttleModel, SuppliersModel } from '../../../../../shared/models';
import { AdcSuppliers } from '../../../../../shared/lists';
import { AdcsApiService, ShuttleApiService } from '../../../../../shared/http';
import { AdcsUtilsService, NamespaceService } from '../../../../../shared/services/utils';
import { BaseComponent } from '../../../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
    LanguageService,
    LoadingService,
    LogService,
    UtilsService,
    ValidationService
} from '../../../../../../../core/services';
import { domainOrIpValidation, nameValidation, portRangeValidation } from '../../../../../../../core/directives';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-page2',
    templateUrl: './adc-add-details.component.html',
    styleUrls: ['./adc-add-details.component.scss']
})
export class AdcAddDetailsComponent extends BaseComponent {
    /**
     * Selected supplier title for the form
     */
    public selectedSupplier: SuppliersModel;

    /**
     * Store the current step.
     */
    public activeStep: number = 1;

    /**
     * New Loadbalancer FormGroup
     */
    public formGroup: FormGroup;

    /**
     * Store the shuttle dropdown content for use in the view.
     */
    public dropdown: any = {
        shuttles: [],
        namespaces: []
    };

    /**
     * Store the selected namespace key.
     */
    private selectedNamespaceKey: number;

    /**
     * Store the list of ADCs for the selected namespace.
     * @private
     */
    private adcsList: any = [];

    /**
     * Store the list of shuttles from the API.
     * @private
     */
    private shuttleList: ShuttleModel[] = [];

    /**
     * Store the list of namespaces.
     * @private
     */
    private readonly namespaceList: any = [];

    constructor(
        private activatedRoute: ActivatedRoute,
        private adcsApiService: AdcsApiService,
        private adcsUtilService: AdcsUtilsService,
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private loadingService: LoadingService,
        private logService: LogService,
        private namespaceService: NamespaceService,
        private router: Router,
        private shuttleApiService: ShuttleApiService,
        private utilsService: UtilsService,
        private validationService: ValidationService
    ) {
        super();

        this.selectedNamespaceKey = this.namespaceService.selectedNamespace.key;
        this.namespaceList = this.namespaceService.namespaces;

        this.setUpForm();
        this.setUpFormSubscribers();
        this.setUpPageByVendor();
        this.setUpPageData();
    }

    /**
     * Go back a step in the process.
     */
    public backStep(): void {
        this.activeStep--;
    }

    /**
     * Go forward a step in the process.
     */
    public nextStep(): void {
        this.activeStep++;
    }

    /**
     * Go to a specific step in the process.
     * @param number
     */
    public setStep(number: number): void {
        this.activeStep = number;
    }

    /**
     * Set namespace Id
     * @param $event
     */
    public setNamespaceId($event: any): void {
        this.selectedNamespaceKey = $event;
    }

    /**
     * Show namespace in form.
     */
    public showNamespace(): boolean {
        return this.namespaceService.selectedNamespace.key === 0;
    }

    /**
     * Set back button disabled state.
     */
    public isBackButtonDisabled(): boolean {
        if (this.selectedSupplier?.name === 'genericAppliance') {
            return this.activeStep === 3;
        }

        return this.activeStep === 1;
    }

    /**
     * Check if the button is disabled based on the form step state.
     */
    public isNextButtonDisabled(): boolean {
        return this.formGroup.get('step' + this.activeStep).invalid;
    }

    /**
     * Returns the form group as FormGroup.
     * @param group
     */
    public getFormGroup(group: any): FormGroup {
        return this.formGroup.get(group) as FormGroup;
    }

    /**
     * Get shuttle label
     */
    public getShuttleDetails(property?: string): string {
        const shuttleId = this.formGroup.get('step1.shuttleId').value;
        if (shuttleId) {
            const selectedShuttle = this.shuttleList.find((item: ShuttleModel) => item.id === shuttleId);
            if (property) {
                return selectedShuttle[property];
            } else {
                return selectedShuttle.label;
            }
        }
    }

    /**
     * Get namespace label
     */
    public getNamespaceLabel(): string {
        const namespaceId = this.formGroup.get('step3.namespaceID').value;

        return this.adcsUtilService.getNamespaceFriendlyName(this.namespaceList, namespaceId);
    }

    /**
     * Set tag value in form.
     * @param event
     */
    public setTagsFormValue(event: any): void {
        this.formGroup.get('step4.tags').setValue(event);
    }

    /**
     * Submit ADC
     */
    public submitAdc(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const submitValues = this.formatFormValues(this.formGroup.value);
            this.adcsApiService.create(this.selectedNamespaceKey, submitValues)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        const timerId = setTimeout(() => {
                            this.router.navigate(['/cm/adcs/list']).then().catch();
                            clearTimeout(timerId);
                        }, 1500);
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error(
                            'AddLoadbalancerComponent: submitLoadbalancer',
                            'Error submitting data', error
                        );
                        observer.error(error);
                    },
                    complete: (): void => {
                        this.loadingService.loadingButtonEmitter.next(false);
                        observer.next();
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Format the values from the three steps.
     * @param formValues
     * @private
     */
    private formatFormValues(formValues: any): any {
        formValues.step2 = {
            username: formValues.step2?.username,
            password: formValues.step2?.password,
            apiIpAddress: formValues.step2?.ipAddress,
            apiPort: formValues.step2?.port.toString()
        };

        delete formValues.step2.ipAddress;
        delete formValues.step2.port;

        const submitValues = { ...formValues.step1, ...formValues.step2, ...formValues.step3, ...formValues.step4 };

        submitValues.port = submitValues.port.toString();
        submitValues.vendor = this.selectedSupplier.vendorId;
        submitValues.sidecarID = submitValues.shuttleId;

        delete submitValues.shuttleId;

        return submitValues;
    }

    /**
     * Set up the add load balancer form.
     * @private
     */
    private setUpForm(): void {
        this.formGroup = this.formBuilder.group({
            step1: this.formBuilder.group({
                shuttleId: [null, Validators.required],
            }),
            step2: this.formBuilder.group({
                username: [null, [Validators.maxLength(254), Validators.required]],
                password: [null, Validators.required],
                ipAddress: [null, [Validators.required, domainOrIpValidation]],
                port: [443, [Validators.required, portRangeValidation]],
            }),
            step3: this.formBuilder.group({
                label: [null, [nameValidation, Validators.required]],
                ipAddress: [null, [Validators.required, domainOrIpValidation]],
                port: [443, [Validators.required, portRangeValidation]],
                namespaceID: [this.selectedNamespaceKey, Validators.required]
            }),
            step4: this.formBuilder.group({
                notes: [null, Validators.maxLength(256)],
                tags: []
            }),
            step5: this.formBuilder.group({
                submit: []
            })
        });
    }

    /**
     * Set up form subscribers.
     * @private
     */
    private setUpFormSubscribers(): void {
        this.namespaceService.selectedEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (namespace: any): void => {
                    this.selectedNamespaceKey = namespace.key;
                    this.formGroup.get('step3.namespaceID').setValue(this.selectedNamespaceKey);
                    this.setUpDropdownContent();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'AdcAddDetailsComponent: setupSubscribers',
                        'Unable to get selected namespace', error
                    );
                }
            });

        this.formGroup.get('step2').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (values: any): void => {
                    this.utilsService.debounce('step2', () => {
                        if (this.selectedSupplier?.name !== 'genericAppliance') {
                            const shuttle = this.formGroup.get('step1.shuttleId').value;
                            if (this.isIpAddress(values.ipAddress)) {
                                this.validatePortAndIpForSelectedShuttle(this.formGroup.get('step2'),
                                    values.ipAddress, values.port, shuttle);
                                this.validateIPinSubnet(this.formGroup.get('step2'), values.ipAddress, shuttle);
                            }

                            const step2Address = this.formGroup.get('step2.ipAddress');
                            if (step2Address.valid) {
                                this.formGroup.get('step3.ipAddress').setValue(step2Address.value);
                            }
                        }
                    }, 350);
                }
            });

        this.formGroup.get('step3').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (values: any): void => {
                    this.utilsService.debounce('step3', () => {
                        this.validateLabelInput(values.label);
                        if (this.selectedSupplier?.name !== 'genericAppliance') {
                            const shuttle = this.formGroup.get('step1.shuttleId').value;
                            this.validatePortAndIpForSelectedShuttle(this.formGroup.get('step3'), values.ipAddress, values.port, shuttle);
                            this.validateIPinSubnet(this.formGroup.get('step3'), values.ipAddress, shuttle);
                        }
                    }, 250);
                }
            });
    }

    /**
     * Check if the value is an ipv4, ipv6 or domain name.
     * @param value
     * @private
     */
    private isIpAddress(value: string): boolean {
        return ValidationService.ipDomainValidator(value);
    }

    /**
     * Set up the page by vendor.
     * @private
     */
    private setUpPageByVendor(): void {
        const vendorId = this.activatedRoute.snapshot.paramMap.get('vendorId');
        const selectedSupplier = AdcSuppliers.find(item => item.vendorId === parseInt(vendorId, 10));
        if (selectedSupplier) {
            this.selectedSupplier = selectedSupplier;
            if (selectedSupplier?.name === 'genericAppliance') {
                this.activeStep = 3;
                this.formGroup.get('step1.shuttleId').disable();
                this.formGroup.get('step2').disable();
            }

            if (selectedSupplier?.name === 'lbEnterprise') {
                this.formGroup.get('step2.port').setValue(9000);
                this.formGroup.get('step3.port').setValue(9443);
                this.formGroup.get('step2').updateValueAndValidity();
                this.formGroup.get('step3').updateValueAndValidity();
            }
        }
    }

    /**
     * Set up page data with API response.
     * @private
     */
    private setUpPageData(): void {
        forkJoin({
            shuttles: this.shuttleApiService.getShuttleList(this.selectedNamespaceKey),
            adcs: this.adcsApiService.getAll(this.selectedNamespaceKey)
        })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.shuttleList = data.shuttles;
                    this.adcsList = data.adcs;
                    this.setUpDropdownContent();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('AddLoadbalancer:setUpPageData', 'Error setting up the page.', error);
                }
            });
    }

    /**
     * Setup dropdown arrays object.
     * @private
     */
    private setUpDropdownContent(): void {
        const sortedByStatus = this.utilsService.sortArrayByProperty(this.shuttleList, 'status', ['online', 'offline']);
        this.dropdown.shuttles = this.adcsUtilService.getFormattedShuttleDropdown(sortedByStatus);
        this.dropdown.namespaces = this.adcsUtilService.getNamespaceDropdownContent(this.namespaceList);
        // Reset the selected shuttle upon namespace change!
        this.formGroup.get('step1.shuttleId').setValue(null);
    }

    /**
     * Validate label input.
     * @param value
     * @private
     */
    private validateLabelInput(value: any): void {
        const foundMatch = this.adcsList.find((adc: AdcModel): boolean => adc.label?.toLowerCase() === value.toLowerCase());
        if (foundMatch) {
            this.formGroup.get('step3.label').setErrors({ 'duplicateValue': `${this.languageService.translate('duplicate_adc_label')}` });
        }
    }

    /**
     * Validate inputs.
     * @param formGroup
     * @param ipAddress
     * @param port
     * @param shuttleId
     * @private
     */
    private validatePortAndIpForSelectedShuttle(formGroup: any, ipAddress: string, port: number, shuttleId: any): void {
        this.adcsUtilService.validatePortAndIpForSelectedShuttle(this.adcsList, formGroup as FormGroup, ipAddress, port, shuttleId);
    }

    /**
     * Validate IP in subnet
     * @param formGroup
     * @param ipAddress
     * @param shuttleId
     * @private
     */
    private validateIPinSubnet(formGroup: any, ipAddress: string, shuttleId: number): void {
        this.adcsUtilService.validateIPinSubnet(this.shuttleList, formGroup as FormGroup, ipAddress, shuttleId);
    }
}
